'use client';
import { useRef, useEffect, useState, useMemo } from 'react';
import { usePathname, useSearchParams, useRouter } from 'next/navigation';
import useSWRInfinite from 'swr/infinite';
import { CategoryText } from '../shared/CategoryText';
import { TopTabLink } from '../shared/topTabLink';
import StyleToggle from '../shared/styleToggle';
import { TabLink } from '../shared/tabLink';
import { InfiniteScrollWrapper } from '../shared/infiniteScroll/infiniteScroll';
import { getValidatedParams } from '@/lib/utils/sanitizeSearchQuery';
import { getLocalizedPath } from '../shared/formatURL';
import Link from 'next/link';
import { Video, Image as ImageIcon } from 'lucide-react';
import { gifReady } from '../shared/generators/generators';

interface Post {
  _id: string;
  author: string;
  createdAt: string;
  imageUrl: string;
  videoUrl: string;
  thumbnailUrl: string;
  likes: number;
  model: string;
  tagGroups: string[];
  updatedAt: string;
  userLiked: boolean;
  views: number;
}

interface PageData {
  info: {
    count: number;
    next: string | null;
    pages: number;
    prev: string | null;
  };
  results: Post[];
}

export function SearchPage({
  contentType,
  translations,
  modalTranslations,
  styleToggleTranslations,
  signInModalTranslations
}: {
  contentType: string;
  translations: any;
  modalTranslations: any;
  styleToggleTranslations: any;
  signInModalTranslations: any;
}) {
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();

  // Ref for the scrollable container
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const options = [
    { key: 'top1', label: '1 Month Top' },
    { key: 'top3', label: '3 Month Top' },
    { key: 'top6', label: '6 Month Top' }
  ];
  const validatedParams = getValidatedParams({
    filter: searchParams.get('filter') || undefined,
    style: searchParams.get('style') || undefined,
    search: searchParams.get('search') || undefined
  });
  // Use the validated params directly instead of re-validating
  const styleParam = searchParams.get('style') || 'all';
  const processedStyleParam = styleParam.includes(',') ? 'all' : styleParam;

  const [currTab, setCurrTab] = useState(validatedParams.filter || 'hot');
  const [currGenerator, setCurrGenerator] = useState(processedStyleParam);

  const queryParam = validatedParams.search || '';
  const newURL =
    contentType === 'gif' ? getLocalizedPath('gif/search') : getLocalizedPath('post/search');
  //update search endpoint for gif
  const searchEndpoint = `${process.env.NEXT_PUBLIC_BACKEND_URL}/post/search`;

  // Define URL construction function
  const constructUrl = (tab: string, generator: string, index: number): string => {
    const baseParams = new URLSearchParams({
      limit: '20',
      ...(generator !== 'all' && { style: generator }),
      ...(queryParam && { searchQuery: queryParam }),
      ...(contentType === 'gif' && { type: 'gif' })
    });

    const tabParams: { [key: string]: string } = {
      new: 'new',
      hot: 'hot',
      top1: 'top1',
      top3: 'top3',
      top6: 'top6'
    };

    if (!tabParams[tab]) {
      throw new Error('Invalid tab provided');
    }

    baseParams.append('sort', tabParams[tab]);
    return `${searchEndpoint}?${baseParams.toString()}`;
  };

  const updateStyleParam = (newStyle: any) => {
    setCurrGenerator(newStyle);
  };

  useEffect(() => {
    if (styleParam.includes(',')) {
      const newSearchParams = new URLSearchParams(Array.from(searchParams.entries()));
      newSearchParams.set('style', 'all');
      router.replace(`${pathname}?${newSearchParams.toString()}`);
      setCurrGenerator('all');
    }
  }, [styleParam]);

  return (
    <div className="relative">
      {/* Top GIF/Image selector */}
      {gifReady && (
        <div className="mx-auto -mt-4 flex flex-col sm:-mt-6">
          <div className="flex flex-row justify-center">
            <Link
              prefetch={false}
              href={{
                pathname: translations.path + 'post/search',
                query: {
                  search: queryParam,
                  style: styleParam
                }
              }}
              className={`rounded-md text-sm font-bold ${
                contentType === 'image' ? 'text-white' : 'text-gray-300'
              }
           `}
            >
              <div className="flex items-center justify-center">
                <ImageIcon
                  className={`mr-2 h-4 w-4 md:mr-3 ${
                    contentType === 'image' ? 'text-crp-red' : 'text-gray-300'
                  }`}
                />
                {translations.images || 'Images'}
              </div>
            </Link>
            <div className="mx-4 h-5 w-[1px] self-center bg-[#3C3C3C]" />
            <Link
              prefetch={false}
              href={{
                pathname: translations.path + 'gif/search',
                query: {
                  search: queryParam,
                  style: styleParam
                }
              }}
              className={`rounded-md text-sm font-bold ${
                contentType === 'gif' ? 'text-white' : 'text-gray-300'
              }
           `}
            >
              <div className="flex items-center justify-center">
                <Video
                  className={`mr-2 h-5 w-5 md:mr-3 ${
                    contentType === 'gif' ? 'text-crp-red' : 'text-gray-300'
                  }`}
                />
                {translations.gifs || 'GIFs'}
              </div>
            </Link>
          </div>
          <div className="mb-4 mt-2 w-[100vw] border-b-[0.5px] border-[#3C3C3C]" />
        </div>
      )}
      <div className="mx-auto px-5 xl:max-w-[88rem]">
        {/* Tab Category Text */}
        <CategoryText
          sortBy={currTab === 'top1' || currTab === 'top3' || currTab === 'top6' ? 'top' : currTab}
          style={currGenerator ? [currGenerator] : ['all']}
          pathCode={translations.key}
          queryParam={queryParam}
          translations={translations}
          contentType={contentType}
        />
        <div className={`z-10 flex items-center`}>
          {/* Hot/New/Top/All Tabs */}
          <div
            ref={scrollContainerRef}
            className="xs:mx-0 mx-[-18px] flex items-center overflow-hidden overflow-x-scroll whitespace-nowrap scrollbar-hide sm:w-full"
          >
            <div className="ml-5 flex">
              <TabLink
                label="hot"
                current={currTab}
                setCurrent={setCurrTab}
                newURL={newURL}
                contentType={contentType}
                currTab={currTab}
                currGenerator={currGenerator}
                queryParam={queryParam}
                translations={translations}
              />
            </div>
            <TabLink
              label="new"
              current={currTab}
              setCurrent={setCurrTab}
              newURL={newURL}
              contentType={contentType}
              currTab={currTab}
              currGenerator={currGenerator}
              queryParam={queryParam}
              translations={translations}
            />
            <TopTabLink
              label="top"
              current={currTab}
              setCurrent={setCurrTab}
              newURL={newURL}
              translations={translations}
              options={options}
            />

            <div className="mr-2.5 inline-block h-4 border-r border-white"></div>
            <TabLink
              label="all"
              current={currGenerator}
              setCurrent={setCurrGenerator}
              isTab={false}
              newURL={newURL}
              contentType={contentType}
              currTab={currTab}
              currGenerator={currGenerator}
              queryParam={queryParam}
              translations={translations}
            />
            {/* Styles toggle modal */}

            <StyleToggle
              styleParam={styleParam}
              updateStyleParam={updateStyleParam}
              translations={styleToggleTranslations}
              contentType={contentType}
            />
          </div>
        </div>

        {/* Rest of your existing UI (slider, expand/fullscreen buttons, etc.) */}
      </div>
      {/* Content area */}
      <InfiniteScrollWrapper
        contentType={contentType}
        translations={translations}
        modalTranslations={modalTranslations}
        signInModalTranslations={signInModalTranslations}
        currTab={currTab}
        currGenerator={currGenerator}
        constructUrlFn={constructUrl}
        queryParam={queryParam}
      />
    </div>
  );
}
